import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import _ from 'underscore';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { CreatePlantInput } from 'views/feed-management/features/plants/types/create-plant-input.type';
import { Plant } from 'views/feed-management/features/plants/types/plant.type';

import Breadcrumbs from 'components/breadcrumbs';
import CheckboxSelectItem from 'components/checkbox-select-item';
import Dialog from 'components/dialog';
import FilterSidebar from 'components/filter-sidebar';
import FlexBox from 'components/flex-box';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Pagination from 'components/pagination';
import Paper from 'components/paper';
import Select from 'components/select';
import TextField from 'components/text-field';
import { usePagination } from 'hooks/use-pagination';
import { MarketId } from 'types/market-id.enum';
import { postFeedManagementPlant } from 'views/feed-management/features/plants/api/create-plants';
import { getFeedManagementMarkets } from 'views/feed-management/features/plants/api/get-markets';
import { getFeedManagementPlants } from 'views/feed-management/features/plants/api/get-plants';
import FeedManagementTabsHeader from '../../components/feed-management-tabs-header';
import { deleteBestmixPlant } from './api/delete-plants';

type Props = {};

const ListPlants: React.FC<Props> = () => {
  const history = useHistory();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const createDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const removeDialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const PAGINATION_LIMIT = 10;
  const PAGE_NAME_ID = 'feedManagement.listPlants';

  // FORMS
  const { handleSubmit, control, watch, setValue } = useForm<CreatePlantInput>({
    defaultValues: {
      code: '',
      name: '',
      markets: [],
    },
    shouldUnregister: false,
  });

  // STATE
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);

  // FETCH DATA
  const { data: plants, isLoading } = useGet(['getFeedManagementPlants'], () => getFeedManagementPlants());
  const { data: markets } = useGet(['getFeedManagementMarkets'], () => getFeedManagementMarkets());

  // POST DATA
  const createPlant = usePost<Plant, CreatePlantInput>(
    (request) => postFeedManagementPlant(request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getFeedManagementPlants']
      });
      createDialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const removePlant = usePost<number, { code: string; idx: number }>(
    (request) => deleteBestmixPlant(request.code, request.idx),
    (response) => {
      queryClient.invalidateQueries({
        queryKey: ['getFeedManagementPlants']
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      if (!!response) removeDialogRef.current[response].setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS
  const handleCreatePlant = (data: CreatePlantInput) => {
    createPlant.mutate(data);
  };

  const handleDeletePlant = (code: string, idx: number) => {
    removePlant.mutate({ code, idx });
  };

  const handleOnClick = (url: string) => {
    history.push(url);
  };

  // RENDERS
  const renderRows = () => {
    const rows = _.chain(plants)
      .sortBy((p) => p.code)
      .value();
    return rows;
  };

  const checkAll = () => {
    let marketIds: MarketId[] = [];
    if (!!markets) {
      marketIds = watch('markets').length !== markets?.length ? markets?.map((m) => Number(m.id)) : [];
    }
    setValue('markets', marketIds);
  };

  return (
    <>
      <FeedManagementTabsHeader
        intro={
          <Typography color="white">
            <FormattedMessage id="general.confidential" />
          </Typography>
        }
        utils={<Breadcrumbs />}
      />
      <Paper transparent>
        <FilterSidebar>
          <Dialog
            ref={createDialogRef}
            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.title` })}
            button={
              <Button>
                <FormattedMessage id={`${PAGE_NAME_ID}.button`} />
              </Button>
            }>
            <form onSubmit={handleSubmit(handleCreatePlant)}>
              {createPlant.isPending && <LoadingSpinner overlay />}
              <FlexBox>
                <TextField
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.codeLabel` })}
                  controller={{
                    name: 'code',
                    control: control,
                    rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                  }}
                />
                <TextField
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.nameLabel` })}
                  controller={{
                    name: 'name',
                    control: control,
                    rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                  }}
                />
                <Select
                  multiple
                  dark
                  fullWidth
                  checkAll={{
                    value: watch('markets')?.length === markets?.length,
                    onChange: checkAll,
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.marketsLabel` })}
                  controller={{
                    name: 'markets',
                    control: control,
                  }}>
                  {markets?.map((market) => (
                    <CheckboxSelectItem checked={watch('markets')?.includes(market.id)} value={market.id} key={market.id}>
                      {market.name}
                    </CheckboxSelectItem>
                  ))}
                </Select>

                <Box display={'flex'} justifyContent={'end'}>
                  <Button type="submit" size="medium">
                    <FormattedMessage id={`general.submit`} values={{ type: '' }} />
                  </Button>
                </Box>
              </FlexBox>
            </form>
          </Dialog>
        </FilterSidebar>
        <List
          options={[
            { id: 'code', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.codeLabel` }) },
            { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nameLabel` }) },
            { id: 'markets', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.marketsLabel` }) },
            {
              id: 'actions',
              label: '',
              align: 'center',
              autoWidth: true,
            },
          ]}
          orderBy="name"
          isLoading={isLoading}
          noResult={{
            show: renderRows().length <= 0,
            text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
          }}>
          {renderRows()
            .slice(from, to)
            .map((row, idx) => (
              <ListRow key={row.code} onClick={() => handleOnClick(`/feed-management/plants/${row.code}`)}>
                <ListCell align="left">{row.code}</ListCell>
                <ListCell align="left" sx={{ whiteSpace: 'break-spaces !important' }}>
                  {row.name}
                </ListCell>
                <ListCell align="left" sx={{ whiteSpace: 'break-spaces !important' }}>
                  {row.markets.map((market) => market.name).join(', ')}
                </ListCell>
                <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Box display="flex" justifyContent={'space-between'} gap={2} alignItems="center" sx={{ width: '100%' }}>
                    <Dialog
                      ref={(el) => {
                        if (el) return (removeDialogRef.current[idx] = el);
                      }}
                      maxWidth="xs"
                      title={`Delete (${row.name})`}
                      button={
                        <Box ml={4}>
                          <Button size="small">
                            <FormattedMessage id="general.delete" />
                          </Button>
                        </Box>
                      }>
                      <Box display={'flex'} flexDirection={'column'} gap={4}>
                        <Box>
                          <FormattedMessage id="general.deleteComment" />
                        </Box>
                        <Box display={'flex'} justifyContent={'end'} gap={4}>
                          <Button variant="outlined" type="submit" size="small" onClick={() => removeDialogRef.current[idx]?.close()}>
                            <FormattedMessage id="general.cancel" />
                          </Button>
                          <Button type="submit" size="small" onClick={() => handleDeletePlant(row.code, idx)}>
                            <FormattedMessage id="general.delete" />
                          </Button>
                        </Box>
                      </Box>
                    </Dialog>
                    <Button size="small" onClick={() => handleOnClick(`/feed-management/plants/${row.code}`)}>
                      <FormattedMessage id="general.edit" values={{ type: '' }} />
                    </Button>
                  </Box>
                </ListCell>
              </ListRow>
            ))}
        </List>
        <Pagination currentPage={currentPage} pages={Math.ceil(renderRows().length / PAGINATION_LIMIT)} onClick={handlePagination} />
      </Paper>
    </>
  );
};

export default ListPlants;
